import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCategoryConfig, STATUS_CONFIG } from "@/lib/taskCategoryConfig";

import { format, formatDistanceToNow, differenceInDays } from "date-fns";
import { 
  Calendar as CalendarIcon, 
  User, 
  Clock, 
  Tag, 
  Link2, 
  Paperclip, 
  MessageSquare,
  Edit,
  Trash2,
  Copy,
  Ban,
  CheckCircle2,
  ListTodo,
  Network,
  Layers,
  FileText,
  PenTool,
  Activity
} from "lucide-react";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TaskAssigneeSection } from "./TaskAssigneeSection";
import { AddAssigneeDialog } from "./AddAssigneeDialog";
import { TaskComments } from "./TaskComments";
import { TaskCommentsThreaded } from "./comments/TaskCommentsThreaded";
import { TaskActivityFeed } from "./TaskActivityFeed";
import { TaskWatchButton } from "./TaskWatchButton";
import { SubtaskList } from "./SubtaskList";
import { TaskDependencies } from "./TaskDependencies";
import { TaskPhaseProgress } from "./TaskPhaseProgress";
import { CustomFieldsDisplay } from "./CustomFieldsDisplay";
import { useAuth } from "@/contexts/AuthContext";
import { DocumentsList } from "@/components/documents/DocumentsList";
import SignaturesTab from "@/components/signatures/SignaturesTab";

interface TaskDetailViewProps {
  task: any;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (task: any) => void;
  onDelete: (taskId: string) => void;
  onUpdateTask: () => void;
}

export const TaskDetailView = ({ 
  task, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete,
  onUpdateTask 
}: TaskDetailViewProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [assignees, setAssignees] = useState<any[]>([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [addAssigneeOpen, setAddAssigneeOpen] = useState(false);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [phaseCounts, setPhaseCounts] = useState({ total: 0, completed: 0 });

  // Load assignees and custom fields from task_assignments table
  useEffect(() => {
    if (isOpen && task?.id) {
      loadAssignees();
      loadCustomFields();
      loadPhaseCounts();
    }
  }, [isOpen, task?.id]);

  const loadPhaseCounts = async () => {
    try {
      const { data, error } = await supabase
        .from('task_phases')
        .select('status')
        .eq('task_id', task.id);

      if (error) throw error;
      
      const total = data?.length || 0;
      const completed = data?.filter(p => p.status === 'completed').length || 0;
      setPhaseCounts({ total, completed });
    } catch (error) {
      console.error('Error loading phase counts:', error);
    }
  };

  const loadCustomFields = async () => {
    try {
      const { data, error } = await supabase
        .from('task_custom_fields')
        .select('*')
        .eq('task_id', task.id);

      if (error) throw error;
      setCustomFields(data || []);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    }
  };

  // Early return AFTER all hooks
  if (!task) return null;

  const category = getCategoryConfig(task.category_type || 'other');
  const CategoryIcon = category.icon;
  const status = STATUS_CONFIG[task.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.not_started;
  
  const checkpoints = task.checkpoints || [];
  const completedCheckpoints = checkpoints.filter((cp: any) => cp.completed).length;
  const progress = checkpoints.length > 0 ? (completedCheckpoints / checkpoints.length) * 100 : 0;
  
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';
  const daysUntilDue = task.due_date ? differenceInDays(new Date(task.due_date), new Date()) : null;

  const loadAssignees = async () => {
    setLoadingAssignees(true);
    try {
      const { data, error } = await supabase
        .from('task_assignments')
        .select(`
          id,
          status,
          assigned_at,
          accepted_at,
          started_at,
          completed_at,
          una_member_id,
          una_members!inner (
            id,
            full_name,
            member_role,
            email,
            phone,
            user_id
          )
        `)
        .eq('task_id', task.id)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      const formattedAssignees = (data || []).map((assignment: any) => {
        const member = assignment.una_members;
        return {
          id: member.id,
          full_name: member.full_name,
          member_role: member.member_role,
          assignment_status: assignment.status,
          assigned_at: assignment.assigned_at,
          accepted_at: assignment.accepted_at,
          started_at: assignment.started_at,
          completed_at: assignment.completed_at,
          email: member.email,
          phone: member.phone,
          assignment_id: assignment.id,
          una_member_id: member.id,
          user_id: member.user_id,
        };
      });

      setAssignees(formattedAssignees);
    } catch (error) {
      console.error('Error loading assignees:', error);
    } finally {
      setLoadingAssignees(false);
    }
  };

  const handleAssigneesUpdate = () => {
    loadAssignees();
    onUpdateTask();
  };

  const handleCompleteTask = async () => {
    try {
      const oldStatus = task.status;
      const newStatus = task.status === 'completed' ? 'not_started' : 'completed';
      
      const { error } = await supabase
        .from('custom_tasks')
        .update({
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        })
        .eq('id', task.id);

      if (error) throw error;

      // Notify watchers about status change
      try {
        await supabase.functions.invoke('notify-task-status-change', {
          body: {
            taskId: task.id,
            oldStatus,
            newStatus,
            userId: user?.id
          }
        });
      } catch (notifError) {
        console.error('Error notifying watchers:', notifError);
      }

      if (newStatus === 'completed') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast({
          title: "🎉 Task Completed!",
          description: "Great work on completing this task!",
        });
      }

      onUpdateTask();
      if (newStatus === 'completed') {
        onClose();
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: "Error",
        description: "Failed to update task",
        variant: "destructive",
      });
    }
  };
  
  const calculateTimeToComplete = (task: any) => {
    if (!task.created_at) return 0;
    const startTime = new Date(task.created_at).getTime();
    const endTime = new Date().getTime();
    return Math.round((endTime - startTime) / (1000 * 60)); // minutes
  };

  const handleToggleCheckpoint = async (checkpointId: string) => {
    try {
      const updatedCheckpoints = checkpoints.map((cp: any) =>
        cp.id === checkpointId ? { ...cp, completed: !cp.completed } : cp
      );

      const { error } = await supabase
        .from('custom_tasks')
        .update({ checkpoints: updatedCheckpoints })
        .eq('id', task.id);

      if (error) throw error;

      toast({
        title: "Checkpoint Updated",
        description: "Progress saved successfully",
      });

      onUpdateTask();
    } catch (error) {
      console.error('Error updating checkpoint:', error);
      toast({
        title: "Error",
        description: "Failed to update checkpoint",
        variant: "destructive",
      });
    }
  };

  const handleMarkBlocked = async () => {
    try {
      const oldStatus = task.status;
      const newStatus = 'blocked';
      
      const { error } = await supabase
        .from('custom_tasks')
        .update({ status: newStatus })
        .eq('id', task.id);

      if (error) throw error;

      // Notify watchers about status change
      try {
        await supabase.functions.invoke('notify-task-status-change', {
          body: {
            taskId: task.id,
            oldStatus,
            newStatus,
            userId: user?.id
          }
        });
      } catch (notifError) {
        console.error('Error notifying watchers:', notifError);
      }

      toast({
        title: "Task Blocked",
        description: "Task marked as blocked",
      });

      onUpdateTask();
    } catch (error) {
      console.error('Error blocking task:', error);
      toast({
        title: "Error",
        description: "Failed to block task",
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async () => {
    try {
      const { error } = await supabase
        .from('custom_tasks')
        .insert({
          ...task,
          id: undefined,
          task_title: `${task.task_title} (Copy)`,
          status: 'not_started',
          completed_at: null,
          created_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast({
        title: "Task Duplicated",
        description: "A copy of this task has been created",
      });

      onUpdateTask();
      onClose();
    } catch (error) {
      console.error('Error duplicating task:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate task",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1">
              <div className={cn("p-2 rounded-lg", category.bgColor)}>
                <CategoryIcon className={cn("h-6 w-6", category.color)} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">
                    {category.name}
                  </Badge>
                </div>
                <DialogTitle className="text-2xl">{task.task_title}</DialogTitle>
              </div>
              <TaskWatchButton taskId={task.id} unaId={task.una_id || ''} currentUserId={user?.id || ''} />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(task)}
              className="gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          </div>
        </DialogHeader>

        {/* Meta Information */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <Badge variant="outline" className={cn("gap-1", status.color)}>
            {status.emoji} {status.label}
          </Badge>
          {task.due_date && (
            <div className={cn(
              "flex items-center gap-1",
              isOverdue ? "text-destructive" : "text-muted-foreground"
            )}>
              <CalendarIcon className="h-4 w-4" />
              <span>
                Due: {format(new Date(task.due_date), "MMM d, yyyy")}
                {daysUntilDue !== null && (
                  <span className="ml-1 font-medium">
                    ({daysUntilDue >= 0 ? `${daysUntilDue} days remaining` : `Overdue by ${Math.abs(daysUntilDue)} days`})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>Created {formatDistanceToNow(new Date(task.created_at))} ago</span>
          {task.estimated_hours && (
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>{task.estimated_hours} hours estimated</span>
            </div>
          )}
        </div>

        <Separator />

        {/* Tabbed Content Area */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="documents" className="gap-1">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="signatures" className="gap-1">
              <PenTool className="h-4 w-4" />
              Signatures
            </TabsTrigger>
            <TabsTrigger value="subtasks" className="gap-1">
              <ListTodo className="h-4 w-4" />
              Subtasks
            </TabsTrigger>
            <TabsTrigger value="dependencies" className="gap-1">
              <Network className="h-4 w-4" />
              Dependencies
            </TabsTrigger>
            <TabsTrigger value="phases" className="gap-1">
              <Layers className="h-4 w-4" />
              Phases
              {phaseCounts.total > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {phaseCounts.completed}/{phaseCounts.total}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-4 mt-4">
            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Tags:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {task.tags.map((tag: string, idx: number) => (
                    <Badge key={idx} variant="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Assignees Section */}
            <TaskAssigneeSection
              task={task}
              assignees={assignees}
              onUpdate={handleAssigneesUpdate}
              onAddAssignee={() => setAddAssigneeOpen(true)}
            />

            {/* Description */}
            {task.task_description && (
              <div>
                <h3 className="font-medium mb-2">📝 Description</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {task.task_description}
                </p>
              </div>
            )}

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <CustomFieldsDisplay fields={customFields} />
            )}

            {/* Related Items */}
            {task.related_items && Object.values(task.related_items).some((items: any) => items.length > 0) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Related Items:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(task.related_items).map(([type, items]) => (
                    (items as string[]).map((itemId: string, idx: number) => (
                      <Badge key={`${type}-${idx}`} variant="outline">
                        {type.slice(0, -1)}: {itemId.slice(0, 8)}...
                      </Badge>
                    ))
                  ))}
                </div>
              </div>
            )}

            {/* Checkpoints */}
            {checkpoints.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium">
                    ✅ Checkpoints ({completedCheckpoints} of {checkpoints.length} completed) - {Math.round(progress)}%
                  </h3>
                </div>
                <Progress value={progress} className="h-2 mb-3" />
                <div className="space-y-2">
                  {checkpoints.map((checkpoint: any) => (
                    <div
                      key={checkpoint.id}
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border",
                        checkpoint.completed && "bg-muted/50"
                      )}
                    >
                      <Checkbox
                        checked={checkpoint.completed}
                        onCheckedChange={() => handleToggleCheckpoint(checkpoint.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className={cn(
                          "font-medium",
                          checkpoint.completed && "line-through text-muted-foreground"
                        )}>
                          {checkpoint.title}
                        </p>
                        {checkpoint.notes && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {checkpoint.notes}
                          </p>
                        )}
                        {checkpoint.due_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Due: {format(new Date(checkpoint.due_date), "MMM d, yyyy")}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments (Empty State) */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Attachments:</span>
              </div>
              <div className="text-sm text-muted-foreground">
                No attachments yet
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-4">
            <DocumentsList taskId={task.id} unaId={task.una_id} />
          </TabsContent>

          <TabsContent value="signatures" className="mt-4">
            {task.una_id && <SignaturesTab taskId={task.id} unaId={task.una_id} />}
          </TabsContent>

          <TabsContent value="subtasks" className="mt-4">
            <SubtaskList taskId={task.id} unaId={task.una_id} />
          </TabsContent>

          <TabsContent value="dependencies" className="mt-4">
            <TaskDependencies taskId={task.id} userId={user?.id || ''} />
          </TabsContent>

          <TabsContent value="phases" className="mt-4">
            <TaskPhaseProgress taskId={task.id} unaId={task.una_id || ''} />
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <TaskActivityFeed taskId={task.id} unaId={task.una_id || ''} />
          </TabsContent>
        </Tabs>

        <Separator />

        {/* Footer Actions */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkBlocked}
              className="gap-2"
            >
              <Ban className="h-4 w-4" />
              Mark as Blocked
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDuplicate}
              className="gap-2"
            >
              <Copy className="h-4 w-4" />
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm("Are you sure you want to delete this task?")) {
                  onDelete(task.id);
                  onClose();
                }
              }}
              className="gap-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>
          <Button
            onClick={handleCompleteTask}
            className="gap-2"
            variant={task.status === 'completed' ? "outline" : "default"}
          >
            <CheckCircle2 className="h-4 w-4" />
            {task.status === 'completed' ? 'Mark Incomplete' : 'Complete Task'}
          </Button>
        </div>

        {/* Add Assignee Dialog */}
        <AddAssigneeDialog
          open={addAssigneeOpen}
          onOpenChange={setAddAssigneeOpen}
          task={task}
          existingAssigneeIds={assignees.map(a => a.una_member_id)}
          onSuccess={handleAssigneesUpdate}
        />
      </DialogContent>
    </Dialog>
  );
};
